<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import markdownit from 'markdown-it';
import roleTranslations from '../../services/RoleTranslations';

const { t } = useI18n();

const md = markdownit({ linkify: true });

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    token.attrPush(['target', '_blank']);
    token.attrPush(['class', 'link']);
    return self.renderToken(tokens, idx, options);
};

// Props
const props = defineProps({
    prompt: {
        type: Object,
        default: () => ({})
    }
});

// Emits
const emit = defineEmits(['save', 'close']);

// Refs
const input = ref(null);

// Computed
const text = computed(() => {
    return md.render(props.prompt.text);
});

// Methods
const handleKeydown = (e) => {
    if (e.key === 'Escape') {
        close();
    }
};

const save = () => {
    emit('save', props.prompt.value);
    close();
};

const close = () => {
    emit('close');
};

const formatPaste = (e) => {
    if (props.prompt.id != "bulk-import") {
        return; // Only add ： for bulk import prompts
    }

    e.preventDefault();

    const pastedData = (e.clipboardData || window.clipboardData).getData('text');
    const hadTrailingNewline = /\r?\n$/.test(pastedData);
    const lines = pastedData.split(/\r?\n/);
    if (hadTrailingNewline) {
        lines.pop();
    }

    // Add ： after every known role
    const formattedLines = lines.map((rawLine) => {
        const line = rawLine.trim();
        if (line.includes('：')) {
            return line;
        }
        const hasSeparator = /[・／/]/.test(line);

        if (hasSeparator) {
            const firstRole = line.split(/[・／/]/)[0].trim();

            if (roleTranslations.find(role => role.name_ja === firstRole)) {
                const whitespaceIndex = line.search(/\s/);

                if (whitespaceIndex !== -1) {
                    return `${line.slice(0, whitespaceIndex)}：${line.slice(whitespaceIndex)}`;
                }

                return `${line}：`;
            }
        }

        if (roleTranslations.find(role => role.name_ja === line)) {
            return `${line}：`;
        }

        return line;
    });

    let textData = formattedLines.join('\n');
    if (hadTrailingNewline) {
        textData += '\n';
    }

    // Insert the formatted text at the current cursor position
    const start = input.value.selectionStart;
    const end = input.value.selectionEnd;
    const currentValue = input.value.value;
    input.value.value = currentValue.substring(0, start) + textData + currentValue.substring(end);
    input.value.setSelectionRange(start + textData.length, start + textData.length);
    input.value.dispatchEvent(new Event('input'));
};

// Lifecycle
onMounted(() => {
    document.addEventListener('keydown', handleKeydown);

    nextTick(() => {
        input.value.focus();

        if (props.prompt.value) {
            input.value.setSelectionRange(0, props.prompt.value.length);
        }
    });
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
    <!-- Fond -->
    <div
        class="fixed top-0 left-0 w-full h-full bg-black/50 flex-center"
        :style="{ zIndex: prompt.zIndex || 50 }"
    >
        <!-- Dialog -->
        <Transition appear enter-from-class="scale-95 opacity-0" enter-to-class="scale-100 opacity-100">
            <div
                class="dialog bg-app-surface1 rounded-xl overflow-hidden md:min-w-[360px] md:w-[40%] w-[90%] max-w-[600px] shadow-2xl max-h-[90vh] overflow-y-auto transition-all duration-100 ease-out flex flex-col"
                role="dialog"
                aria-modal="true"
                :aria-label="prompt.title || t('common.confirm')"
            >
            <!-- Header avec titre et X -->
            <div class="flex items-center justify-between px-6 py-4 border-b border-app-border">
                <h2 v-if="prompt.title" class="text-lg font-semibold text-app-text">{{ prompt.title }}</h2>
                <div v-else />
                <button
                    type="button"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-full text-app-text-soft hover:bg-app-border transition-colors"
                    @click="close"
                    :aria-label="t('common.close')"
                    :title="t('common.close')"
                >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <!-- Contenu -->
            <div class="flex-1 px-6 py-5">
                <p class="text-left break-words leading-relaxed text-app-text2" v-html="text"></p>
            </div>

            <div class="px-6 pb-5">
                <textarea
                    v-if="prompt.type == 'textarea'"
                    ref="input"
                    class="w-full textarea"
                    :placeholder="prompt.placeholder"
                    :maxlength="prompt.maxlength || 200000"
                    rows="5"
                    v-model.trim="prompt.value"
                    @keydown.ctrl.enter="save"
                    @paste="formatPaste"
                />
                <input
                    v-else
                    ref="input"
                    class="w-full input"
                    :placeholder="prompt.placeholder"
                    :maxlength="prompt.maxlength || 200000"
                    v-model.trim="prompt.value"
                    @keydown.enter="save"
                />
            </div>

            <!-- Footer -->
            <div class="flex gap-2 pt-3 px-6 py-6 justify-end">
                <button type="button" class="btn btn-cancel !rounded-full" @click="close">{{ t('common.cancel') }}</button>
                <button type="button" class="btn btn-primary !rounded-full" @click="save">{{ t('common.confirm') }}</button>
            </div>
            </div>
        </Transition>
    </div>
</template>